import { addDays, instantToLocalDate, localToInstant } from './time.ts';
import type {
  AdminUnavailability,
  AvailabilityBlock,
  AvailabilityStatus,
  ResolvedSegment,
  RuleOccurrence,
  SegmentSource,
  TimeOff,
} from './types.ts';

/**
 * Resolve a driver's effective availability over [windowStart, windowEnd).
 *
 * Precedence for every instant:
 *   0. Admin "mark unavailable" → UNAVAILABLE
 *   1. Time off            → UNAVAILABLE
 *   2. Date-specific block → its status
 *   3. Recurring occurrence→ its status (when several overlap, the most
 *                            restrictive wins: UNAVAILABLE > TENTATIVE > AVAILABLE)
 *   4. Nothing             → UNKNOWN (never treated as available)
 *
 * Output segments are contiguous, non-overlapping and cover the whole window.
 */
export function resolveAvailability(input: {
  windowStart: number;
  windowEnd: number;
  timeOff: TimeOff[];
  blocks: AvailabilityBlock[];
  occurrences: RuleOccurrence[];
  adminUnavailable?: AdminUnavailability[];
}): ResolvedSegment[] {
  const { windowStart, windowEnd } = input;
  const adminUnavailable = input.adminUnavailable ?? [];
  if (!(windowEnd > windowStart)) return [];

  const timeOff = input.timeOff.map((t) => ({
    id: t.id,
    start: localToInstant(t.startDate, '00:00'),
    end: localToInstant(addDays(t.endDate, 1), '00:00'),
  }));

  const points = new Set<number>([windowStart, windowEnd]);
  const addPoints = (s: number, e: number) => {
    if (s > windowStart && s < windowEnd) points.add(s);
    if (e > windowStart && e < windowEnd) points.add(e);
  };
  for (const t of timeOff) addPoints(t.start, t.end);
  for (const a of adminUnavailable) addPoints(a.start, a.end);
  for (const b of input.blocks) addPoints(b.start, b.end);
  for (const o of input.occurrences) addPoints(o.start, o.end);

  const sorted = [...points].sort((a, b) => a - b);
  const segments: ResolvedSegment[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const s = sorted[i];
    const e = sorted[i + 1];
    const covers = (x: { start: number; end: number }) => x.start <= s && x.end >= e;
    let seg: ResolvedSegment;

    const adminOff = adminUnavailable.find(covers);
    const off = adminOff ? undefined : timeOff.find(covers);
    if (adminOff) {
      seg = { start: s, end: e, status: 'UNAVAILABLE', source: { kind: 'ADMIN_UNAVAILABLE', id: adminOff.id }, vehicleId: null, destinations: [] };
    } else if (off) {
      seg = { start: s, end: e, status: 'UNAVAILABLE', source: { kind: 'TIME_OFF', id: off.id }, vehicleId: null, destinations: [] };
    } else {
      const block = pickBlock(input.blocks.filter(covers));
      if (block) {
        seg = {
          start: s, end: e, status: block.status, source: { kind: 'BLOCK', id: block.id },
          vehicleId: block.vehicleId, destinations: block.destinations,
        };
      } else {
        const occ = pickOccurrence(input.occurrences.filter(covers));
        if (occ) {
          seg = {
            start: s, end: e, status: occ.status,
            source: { kind: 'RULE', id: occ.ruleId, occurrenceDate: occ.occurrenceDate },
            vehicleId: occ.vehicleId, destinations: occ.destinations,
          };
        } else {
          seg = { start: s, end: e, status: 'UNKNOWN', source: { kind: 'NONE' }, vehicleId: null, destinations: [] };
        }
      }
    }

    const prev = segments[segments.length - 1];
    if (prev && prev.end === seg.start && sameSource(prev.source, seg.source) && prev.status === seg.status) {
      prev.end = seg.end;
    } else {
      segments.push(seg);
    }
  }
  return segments;
}

const RESTRICTIVENESS: Record<AvailabilityStatus, number> = { UNAVAILABLE: 3, TENTATIVE: 2, AVAILABLE: 1 };

function pickBlock(candidates: AvailabilityBlock[]): AvailabilityBlock | undefined {
  // The database forbids overlapping blocks; if bad data slips through, be conservative.
  return candidates.sort((a, b) => RESTRICTIVENESS[b.status] - RESTRICTIVENESS[a.status] || a.id.localeCompare(b.id))[0];
}

function pickOccurrence(candidates: RuleOccurrence[]): RuleOccurrence | undefined {
  return candidates.sort((a, b) => RESTRICTIVENESS[b.status] - RESTRICTIVENESS[a.status] || a.ruleId.localeCompare(b.ruleId))[0];
}

export function sameSource(a: SegmentSource, b: SegmentSource): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'NONE') return true;
  if (a.kind === 'RULE' && b.kind === 'RULE') return a.id === b.id && a.occurrenceDate === b.occurrenceDate;
  return (a as { id: string }).id === (b as { id: string }).id;
}

/**
 * Split an interval at local midnights for display (overnight blocks show as
 * two segments on the calendar but remain one record).
 */
export function splitAtMidnight(start: number, end: number): { start: number; end: number; localDate: string }[] {
  const out: { start: number; end: number; localDate: string }[] = [];
  let cursor = start;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const date = instantToLocalDate(cursor);
    const nextMidnight = localToInstant(addDays(date, 1), '00:00');
    if (end <= nextMidnight) {
      out.push({ start: cursor, end, localDate: date });
      break;
    }
    out.push({ start: cursor, end: nextMidnight, localDate: date });
    cursor = nextMidnight;
  }
  return out;
}
